import { useCallback, useSyncExternalStore } from "react";
import { useGlobalQuery } from "../../api/useGlobalQuery";
import { useAppData } from "../../state/AppData";
import { scopesAvailable } from "../../lib/mapScopes";
import { isReachPayload, reachKey, type ReachPayload, type ReachScope } from "../../lib/regionReach";

// The map's "Region reach" overlay choice: one scope at a time, none by default. Held in one shared store so the
// control, the map layer and the legend agree. Not remembered between visits (the default is no overlay).
interface Store {
  selected: string | null; // reachKey of the scope, or null
  primed: boolean; // the picker was used: load the list of scopes
}
let store: Store = { selected: null, primed: false };
const listeners = new Set<() => void>();

function set(next: Store) {
  store = next;
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
function snapshot(): Store {
  return store;
}

export interface RegionReachState {
  selected: string | null;
  select: (name: string | null) => void;
  /** False while the protocol filter is Meshtastic only: the overlay is off and the picker disabled. */
  available: boolean;
  /** Ask for the list of scopes (the picker was opened). */
  prime: () => void;
  /** The stored reach cell; null until it is wanted and has landed. */
  payload: ReachPayload | null;
  pending: boolean;
  /** The selected scope's data, when the cell has it. */
  scope: ReachScope | null;
}

export function useRegionReach(): RegionReachState {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  const select = useCallback((name: string | null) => set({ ...store, selected: name === null ? null : reachKey(name), primed: true }), []);
  const prime = useCallback(() => {
    if (!store.primed) set({ ...store, primed: true });
  }, []);
  const { protocolFilter } = useAppData();
  const available = scopesAvailable(protocolFilter);
  const wanted = available && (s.selected !== null || s.primed);
  const q = useGlobalQuery<ReachPayload | { pending: true }>(["global", "scopeReach"], "/api/audit/scope-reach", 60_000, wanted, false);
  const payload = isReachPayload(q.data) ? q.data : null;
  const pending = !!q.data && (q.data as { pending?: boolean }).pending === true;
  const selected = available ? s.selected : null; // the choice is kept, and shows again when MeshCore is visible
  const scope = payload && selected ? payload.scopes[selected] ?? null : null;
  return { selected, select, available, prime, payload, pending, scope };
}
